/**
 * useUploadPortfolio — "Upload File": send the instances file (and the
 * optional self-perf file), and get back the rows for both tabs.
 *
 *   const { upload, stage, uploading } = useUploadPortfolio({ mode, provider, model, headroom,
 *                                                             discount, policyEngine, billing });
 *   const result = await upload({ instancesFile, selfPerfFile, portfolioName, portfolioId });
 *   // result: { kind: 'rows', portfolioId, created, rows, selfPerfRows, otherCloudRows }
 *   //       | { kind: 'background', portfolioId, created } (a large file)
 *   //       | null                                         (failed — toast shown)
 *
 * The call order is legacy's (see api/uploadApi.js).
 *  - NEW portfolio (no `portfolioId`): the server CREATES it at the first call,
 *    so any later failure deletes it again — silently, as legacy does — and a
 *    broken upload never leaves a half-made portfolio in the sidebar.
 *  - OPENED portfolio (`portfolioId` given): the file goes INTO it (legacy
 *    sends portfolio_id instead of the name). A failure NEVER deletes it —
 *    legacy did (deletePortfolio on any failure), throwing away a saved
 *    portfolio because one file was bad.
 * `created` says which case it was, so the page knows whether the portfolio
 * is its to discard.
 *
 * One try / catch: every reader in uploadApi throws on a failed reply, and
 * `.unwrap()` throws on an HTTP error, so the toast always shows the server's
 * own message when there is one.
 *
 * `stage` says what is happening right now, for the dialog to show.
 *
 * By product (`mode`, default: the running product): CCA asks for the link
 * with `policy_engine` + `is_billing_data` (no headroom) and maps rows with
 * the CCA row rules (quantity / hours); EIA sends `headroom` and maps the EIA
 * way. `billing` = Billing (CUR) mode; `provider` is a cloud — in Billing mode
 * the Billing "Cloud" pick. `discount` is the UI value { onDemand, oneYearRi,
 * threeYearRi }, sent as pricing_model_discount when set. CCA has no Self Perf file, so the page passes none.
 */
import { useState } from 'react';

import { toUploadedRow as toCcaUploadedRow } from '@components/cca/ccaInstanceRows';
import { toSelfPerfRow, toUploadedRow as toEiaUploadedRow } from '@components/eia/instanceRows';
import { useNotify } from '@hooks/useNotify';
import { APP_MODES, resolveAppMode } from '@variant/appMode';

import {
  putToStorage,
  readBackgroundStart,
  readRowCount,
  readSelfPerfLink,
  readUploadLink,
  readValidatedUpload,
  useDeletePortfolioMutation,
  useGetExcelRowCountMutation,
  useGetUploadLinkMutation,
  useInitiateCostAdviceMutation,
  useValidateUploadMutation,
} from '../api/uploadApi';

export const UPLOAD_STAGES = Object.freeze({
  LINK: 'Preparing the upload…',
  INSTANCES: 'Uploading the instances file…',
  SELF_PERF: 'Uploading the Self Perf Assessment file…',
  COUNT: 'Checking the file…',
  READ: 'Reading the rows…',
  BACKGROUND: 'Starting background processing…',
});

export const UPLOAD_FAILED_MESSAGE = 'Upload failed. Please try again.';

export function useUploadPortfolio({
  mode = resolveAppMode(),
  provider,
  model,
  headroom,
  discount,
  policyEngine,
  billing = false,
}) {
  const toUploadedRow = mode === APP_MODES.CCA ? toCcaUploadedRow : toEiaUploadedRow;
  const [getUploadLink] = useGetUploadLinkMutation();
  const [getRowCount] = useGetExcelRowCountMutation();
  const [validateUpload] = useValidateUploadMutation();
  const [initiateCostAdvice] = useInitiateCostAdviceMutation();
  const [deletePortfolio] = useDeletePortfolioMutation();
  const notify = useNotify();
  const [stage, setStage] = useState(null);

  const upload = async ({
    instancesFile,
    selfPerfFile,
    portfolioName,
    portfolioId: existingId,
  }) => {
    const created = !existingId;
    let portfolioId = null;
    try {
      // 1. The server creates the portfolio (or takes the opened one) and
      // hands back a storage link.
      setStage(UPLOAD_STAGES.LINK);
      const link = readUploadLink(
        await getUploadLink({
          mode,
          provider,
          fileName: instancesFile.name,
          portfolioName,
          portfolioId: existingId || undefined,
          model,
          headroom,
          discount,
          policyEngine,
          billing,
        }).unwrap(),
      );
      portfolioId = link.portfolioId;

      // 2. The instances file goes up.
      setStage(UPLOAD_STAGES.INSTANCES);
      await putToStorage(link.uploadUrl, instancesFile);

      // 3–4. The optional self-perf file goes up, into the same portfolio.
      if (selfPerfFile) {
        setStage(UPLOAD_STAGES.SELF_PERF);
        const selfPerfUrl = readSelfPerfLink(
          await getUploadLink({ portfolioId, udfFile: selfPerfFile.name }).unwrap(),
        );
        await putToStorage(selfPerfUrl, selfPerfFile);
      }

      // 5. Normal or large?
      setStage(UPLOAD_STAGES.COUNT);
      const { large } = readRowCount(await getRowCount(portfolioId).unwrap());

      if (large) {
        // 6b. Too big to show: the server processes it in the background, and
        // the sidebar shows its progress (the "%" rows).
        setStage(UPLOAD_STAGES.BACKGROUND);
        const message = readBackgroundStart(
          await initiateCostAdvice({ portfolioId, discount }).unwrap(),
        );
        notify.success(message);
        return { kind: 'background', portfolioId, created };
      }

      // 6a. Read the file(s): rows for Instance Stats and Self Perf Assessment.
      setStage(UPLOAD_STAGES.READ);
      const { rows, selfPerfRows, otherCloudRows } = readValidatedUpload(
        await validateUpload({ portfolioId, discount }).unwrap(),
      );

      notify.success(
        `File uploaded successfully — ${rows.length} instance${rows.length === 1 ? '' : 's'}` +
          (selfPerfFile
            ? ` and ${selfPerfRows.length} self perf row${selfPerfRows.length === 1 ? '' : 's'}`
            : '') +
          ' loaded.',
      );
      return {
        kind: 'rows',
        portfolioId,
        created,
        rows: rows.map((row) => toUploadedRow(row, provider)),
        selfPerfRows: selfPerfRows.map(toSelfPerfRow),
        // Rows for another cloud, kept out of the table: the page offers them
        // as an Excel download (legacy's "Other Cloud Providers" dialog).
        otherCloudRows,
      };
    } catch (error) {
      // Undo the half-made portfolio, silently (legacy deletePortfolio(true)) —
      // only one THIS upload created; an opened portfolio is never deleted.
      if (created && portfolioId) {
        try {
          await deletePortfolio(portfolioId).unwrap();
        } catch {
          // Nothing more to do; the upload error below is what matters.
        }
      }
      notify.error(error, UPLOAD_FAILED_MESSAGE);
      return null;
    } finally {
      setStage(null);
    }
  };

  return { upload, stage, uploading: stage !== null };
}
